import { Button, Center, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { Dropzone, FileRejection, FileWithPath } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { IconCloudUpload, IconFileText, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SvgFileDragAdd } from '@/components/Create_V2/components/Documents/icon';
import { useStepTransition } from '@/hooks/useStepStore';
import { useCreateStore } from '@/store/create';
import { useMatch } from 'react-router-dom';

export const FaqsImportSheet = () => {
  const { datasetModelName } = useCreateStore();
  const [file, setFile] = useState<File | null>(null);
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const matchDatasetInBotPage = useMatch('/:bot_or_assistant/:botId/datasets/:datasetId/data-source/faq');
  const matchDatasetInDatasetPage = useMatch('/datasets/:datasetId/data-source/faq');
  const datasetId = matchDatasetInBotPage?.params?.datasetId || matchDatasetInDatasetPage?.params?.datasetId;

  const { startNextTransition } = useStepTransition();

  // 是否是本地部署模式，是的话，使用本地Text Embedding
  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  const acceptedFiles = (acceptedFiles: FileWithPath[]) => {
    setFile(acceptedFiles[0]);
  };

  const rejectedFiles = (fileRejections: FileRejection[]) => {
    const { errors, file } = fileRejections[0];
    const fileName = file.name;
    const fileError = errors.map((error) => {
      const i18nKey = `errors.dropzone.${error.code}`;
      const translated = t(i18nKey);
      // 如果翻译key不存在，i18next会返回key本身，此时使用原始message
      return translated === i18nKey ? error.message : translated;
    });
    showNotification({
      message: (
        <>
          <Text>{fileName}</Text>
          <Text>{fileError}</Text>
        </>
      ),
      color: 'red',
    });
  };

  const handleFile = async () => {
    if (!file) return;
    startNextTransition(undefined, {
      type: 'import-sheet',
      source: file,
    });
  };

  return (
    <Center>
      <Stack className="min-w-[90%]">
        <Stack>
          <div className="flex items-center justify-between">
            <Text color="gray.8">{t('faqs.import.sheet.title')}</Text>
            <div>
              <a
                href={`${
                  window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL
                }/datasets/${datasetId}/faqs/download-faq-template`}
                className="hover:underline text-sm font-light text-gray-500"
              >
                <Trans
                  i18nKey="faqs.import.sheet.download_template"
                  components={{
                    span: <span className="text-violet-500" />,
                  }}
                />
              </a>
            </div>
          </div>
          <Dropzone
            className="border-none bg-[#FCFCFD] rounded-lg hover:bg-gray-100 cursor-pointer"
            onDrop={acceptedFiles}
            onReject={rejectedFiles}
            loading={false}
            multiple={false}
            accept={{
              'text/plain': ['.txt'],
              'text/markdown': ['.md'],
              'text/html': ['.html'],
              'application/pdf': ['.pdf'],
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              'application/msword': ['.doc'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            }}
          >
            <Stack className="flex items-center justify-center min-h-[153px] pointer-events-none py-10" gap="md">
              {file ? (
                <>
                  <IconFileText size={40} className="text-violet-500" />
                  <Text color="violet.5">{file.name}</Text>
                </>
              ) : (
                <>
                  <Dropzone.Accept>
                    <IconCloudUpload size={60} stroke={1.5} color={theme.colors[theme.primaryColor][6]} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX size={60} stroke={1.5} color={theme.colors.red[6]} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <SvgFileDragAdd />
                  </Dropzone.Idle>
                  <Stack gap={5} className="text-center">
                    <Text fz={20} color="violet.5">
                      {t('faqs.import.sheet.choose_file')}
                    </Text>
                    <Text size="sm" c="gray.5">
                      {t('faqs.import.sheet.description')}
                    </Text>
                  </Stack>
                </>
              )}
            </Stack>
          </Dropzone>
        </Stack>

        {!isLocalMode && (
          <Text className="mb-4" size="sm" c="gray.8">{`${t('dataset.model')}${datasetModelName}`}</Text>
        )}

        <Group justify="center">
          <Button size="md" onClick={handleFile} style={{ fontSize: 14 }} disabled={!file}>
            {t('faqs.import.sheet.upload_file')}
          </Button>
        </Group>
      </Stack>
    </Center>
  );
};
